import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ActionIcon,
  Button,
  Group,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconFlag,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { dateToDateHourString } from '@utils/date';

import {
  Drug,
  DrugsSortBy,
  GetDrugsRequestParams,
} from '@interfaces/drugs.interface';

import {
  useCountDrugsQuery,
  useDeleteDrugMutation,
  useGetDrugsLastImportDateQuery,
  useGetManyDrugsQuery,
} from '@api/drugs.api';
import { isApiError } from '@api/index';

import useLayoutProps from '@hooks/useLayoutProps';
import useStateWithDebounce from '@hooks/useStateWithDebouce';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UpsertDrugForm, {
  UPSERT_DRUG_FORM_MODAL_ID,
} from '@components/drugs/UpsertDrugForm';

export default function DrugsList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const canCreateDrugs = useUserPermissionGuard('DRUGS.CREATE');
  const canEditDrugs = useUserPermissionGuard('DRUGS.UPDATE');
  const canDeleteDrugs = useUserPermissionGuard('DRUGS.DELETE');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetDrugsRequestParams>({
    page: +1,
    pageLength: +20,
    sortBy: 'activeIngredient',
    sortOrder: 'asc',
  });

  const [aicSearch, setAicSearch, aicSearchDebounced] =
    useStateWithDebounce('');
  const [
    aicNameHolderSearch,
    setAicNameHolderSearch,
    aicNameHolderSearchDebounced,
  ] = useStateWithDebounce('');
  const [nameSearch, setNameSearch, nameSearchDebounced] =
    useStateWithDebounce('');
  const [
    activeIngredientSearch,
    setActiveIngredientSearch,
    activeIngredientSearchDebounced,
  ] = useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Drug>>({
    columnAccessor: 'activeIngredient',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as DrugsSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: drugsLastImportDate } = useGetDrugsLastImportDateQuery();
  const { data: drugsCount = { count: 0 }, isLoading: isLoadingCount } =
    useCountDrugsQuery({
      aicSearch: aicSearchDebounced,
      aicNameHolderSearch: aicNameHolderSearchDebounced,
      nameSearch: nameSearchDebounced,
      activeIngredientSearch: activeIngredientSearchDebounced,
    });

  const { data = [], isLoading } = useGetManyDrugsQuery({
    ...filters,
    aicSearch: aicSearchDebounced,
    aicNameHolderSearch: aicNameHolderSearchDebounced,
    nameSearch: nameSearchDebounced,
    activeIngredientSearch: activeIngredientSearchDebounced,
  });

  const [deleteDrug, { isLoading: isLoadingDeleteDrug }] =
    useDeleteDrugMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleAddDrugClick = useCallback(() => {
    openModal({
      modalId: UPSERT_DRUG_FORM_MODAL_ID,
      title: 'Aggiungi farmaco',
      children: <UpsertDrugForm />,
    });
  }, []);

  const handleEditDrugClick = useCallback((drug: Drug) => {
    openModal({
      modalId: UPSERT_DRUG_FORM_MODAL_ID,
      title: 'Modifica farmaco',
      children: <UpsertDrugForm drug={drug} />,
    });
  }, []);

  const handleDeleteDrugClick = useCallback((aic: string) => {
    openConfirmModal({
      title: 'Elimina farmaco',
      children:
        "Stai per eliminare il farmaco. L'operazione non è reversibile.\nContinuare?",

      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deleteDrug(aic).unwrap();

          showNotification({
            title: 'Farmaco eliminato',
            message: 'Farmaco eliminato con successo',
          });
        } catch (e) {
          if (isApiError(e)) {
            if (e.data.code === 'P2003') {
              showNotification({
                title: 'Impossibile eliminare',
                message: 'Il farmaco è in uso in una terapia',
                color: 'red',
                icon: <IconX />,
              });
            } else {
              showNotification({
                title: 'Errore',
                message: e.data.message,
                color: 'red',
                icon: <IconX />,
              });
            }
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <>
        <Group>
          <IconFlag />
          Farmaci {!isLoadingCount ? `(${drugsCount.count})` : ''}
        </Group>
        <Text>
          Ultima importazione Aifa:{' '}
          <Text span fs="italic">
            {drugsLastImportDate
              ? dateToDateHourString(new Date(drugsLastImportDate))
              : 'nessuna importazione eseguita'}
          </Text>
        </Text>
      </>
    ),
    [drugsCount.count, isLoadingCount],
  );

  const rightSection = useMemo(
    () =>
      canCreateDrugs ? (
        <Button leftSection={<IconPlus />} onClick={handleAddDrugClick}>
          Aggiungi farmaco
        </Button>
      ) : (
        <></>
      ),
    [handleAddDrugClick, canCreateDrugs],
  );

  useLayoutProps({
    title,
    rightSection,
  });

  return (
    <>
      <DataTable
        /* General */
        striped
        noRecordsText="Nessun farmaco trovato"
        minHeight={data.length === 0 ? 200 : undefined}
        /* Data */
        fetching={isLoading || isLoadingCount}
        idAccessor="aic"
        columns={[
          {
            accessor: 'aic',
            title: 'AIC',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca AIC"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  aicSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setAicSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={aicSearch}
                onChange={(e) => setAicSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'aicNameHolder',
            title: 'Titolare AIC',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca titolare AIC"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  aicSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setAicNameHolderSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={aicNameHolderSearch}
                onChange={(e) => setAicNameHolderSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'name',
            title: 'Nome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca nome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  nameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={nameSearch}
                onChange={(e) => setNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'activeIngredient',
            title: 'Principio attivo',
            filter: (
              <TextInput
                placeholder="Ricerca principio attivo"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  activeIngredientSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setActiveIngredientSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={activeIngredientSearch}
                onChange={(e) =>
                  setActiveIngredientSearch(e.currentTarget.value)
                }
              />
            ),
          },
          //// {
          ////   accessor: 'measureUnit',
          ////   title: 'Unità di misura',
          //// },
          {
            accessor: 'retailPrice',
            title: 'Prezzo al dettaglio',
            sortable: true,
            render: (record) => `€ ${record.retailPrice.toFixed(2)}`,
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Group wrap="nowrap" gap="xs">
                {canEditDrugs && (
                  <Tooltip label="Modifica farmaco">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => handleEditDrugClick(record)}
                    >
                      <IconPencil />
                    </ActionIcon>
                  </Tooltip>
                )}
                {canDeleteDrugs && (
                  <Tooltip label="Elimina farmaco">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      disabled={isLoadingDeleteDrug}
                      onClick={() => handleDeleteDrugClick(record.aic)}
                    >
                      <IconTrash />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
        records={data}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={drugsCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50, 100]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
